import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormTextarea,
  QueryStateBoundary,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { centsToUsd } from "@/lib/money";
import { formatShortDate } from "@/features/events/eventDate";
import {
  useBooking,
  useCancelBooking,
  useCompleteBooking,
  useConfirmBooking,
  useGenerateBookingInvoice,
  usePatchBooking,
} from "@/api/hooks/useBookings";
import { BOOKING_STATUS_BADGE_VARIANT, BOOKING_STATUS_LABELS } from "@/api/types/booking";

interface BookingDetailDialogProps {
  bookingId: string | null;
  onClose: () => void;
}

/** Booking detail + status transitions + generate-invoice action. Modeled
 * as a Dialog (this codebase's drawer-equivalent — see
 * features/money/RecordPaymentDialog.tsx for the same convention). */
export function BookingDetailDialog({ bookingId, onClose }: BookingDetailDialogProps) {
  const navigate = useNavigate();
  const { data: booking, isLoading, error, refetch } = useBooking(bookingId ?? undefined);

  const [editing, setEditing] = useState(false);
  const [headcount, setHeadcount] = useState("");
  const [occasion, setOccasion] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (booking) {
      setHeadcount(booking.headcount != null ? String(booking.headcount) : "");
      setOccasion(booking.occasion ?? "");
      setNotes(booking.notes ?? "");
    }
    setEditing(false);
  }, [booking]);

  const confirmBooking = useConfirmBooking();
  const cancelBooking = useCancelBooking();
  const completeBooking = useCompleteBooking();
  const generateInvoice = useGenerateBookingInvoice();
  const patchBooking = usePatchBooking();

  async function handleConfirm() {
    if (!booking) return;
    try {
      await confirmBooking.mutateAsync(booking.id);
      toastSuccess("Booking confirmed");
    } catch (err) {
      toastError("Couldn't confirm booking", getErrorMessage(err));
    }
  }

  async function handleCancel() {
    if (!booking) return;
    try {
      await cancelBooking.mutateAsync(booking.id);
      toastSuccess("Booking cancelled");
    } catch (err) {
      toastError("Couldn't cancel booking", getErrorMessage(err));
    }
  }

  async function handleComplete() {
    if (!booking) return;
    try {
      await completeBooking.mutateAsync(booking.id);
      toastSuccess("Booking marked completed");
    } catch (err) {
      toastError("Couldn't complete booking", getErrorMessage(err));
    }
  }

  async function handleGenerateInvoice() {
    if (!booking) return;
    try {
      const invoice = await generateInvoice.mutateAsync(booking.id);
      toastSuccess("Invoice generated", invoice.number);
    } catch (err) {
      toastError("Couldn't generate invoice", getErrorMessage(err));
    }
  }

  async function handleSaveEdits() {
    if (!booking) return;
    try {
      await patchBooking.mutateAsync({
        id: booking.id,
        headcount: headcount.trim() ? Number(headcount) : null,
        occasion: occasion.trim() || null,
        notes: notes.trim() || null,
      });
      toastSuccess("Booking updated");
      setEditing(false);
    } catch (err) {
      toastError("Couldn't update booking", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={!!bookingId} onClose={onClose} ariaLabel="Booking detail">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Booking detail</DialogHeader>
        <DialogBody className="space-y-4">
          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {booking && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-lg font-semibold text-text-primary">{booking.resource_name}</p>
                    <p className="text-sm text-text-secondary">
                      {formatShortDate(booking.booking_date)}
                      {booking.slot_label ? ` · ${booking.slot_label}` : " · Full day"}
                      {booking.extra_hour ? " (+1 hr)" : ""}
                    </p>
                  </div>
                  <Badge variant={BOOKING_STATUS_BADGE_VARIANT[booking.status]}>
                    {BOOKING_STATUS_LABELS[booking.status]}
                  </Badge>
                </div>

                {booking.is_shadow && (
                  <p className="rounded-md bg-bg-hover px-3 py-2 text-sm text-text-muted">
                    This is a Whole House block on this room. Manage it from the primary Whole House booking.
                  </p>
                )}

                <p className="text-sm text-text-secondary">
                  Price: <span className="font-semibold text-text-primary">{centsToUsd(booking.price_cents)}</span>
                </p>

                {editing ? (
                  <div className="space-y-3">
                    <FormField
                      label="Headcount"
                      type="number"
                      min={0}
                      value={headcount}
                      onChange={(e) => setHeadcount(e.target.value)}
                    />
                    <FormField
                      label="Occasion"
                      type="text"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                    />
                    <FormTextarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => void handleSaveEdits()} disabled={patchBooking.isPending}>
                        {patchBooking.isPending ? "Saving…" : "Save"}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-sm text-text-secondary">
                    <p>Headcount: {booking.headcount ?? "—"}</p>
                    <p>Occasion: {booking.occasion ?? "—"}</p>
                    <p>Notes: {booking.notes ?? "—"}</p>
                    {!booking.is_shadow && booking.status !== "cancelled" && booking.status !== "completed" && (
                      <Button size="sm" variant="link" onClick={() => setEditing(true)}>
                        Edit details
                      </Button>
                    )}
                  </div>
                )}

                {booking.invoice_id ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(`/money/${booking.invoice_id}`)}
                  >
                    View invoice
                  </Button>
                ) : (
                  !booking.is_shadow &&
                  (booking.status === "confirmed" || booking.status === "completed") && (
                    <Button size="sm" onClick={() => void handleGenerateInvoice()} disabled={generateInvoice.isPending}>
                      {generateInvoice.isPending ? "Generating…" : "Generate invoice"}
                    </Button>
                  )
                )}
              </>
            )}
          </QueryStateBoundary>
        </DialogBody>
        {booking && !booking.is_shadow && (
          <DialogFooter>
            {booking.status === "inquiry" && (
              <Button variant="secondary" onClick={() => void handleCancel()} disabled={cancelBooking.isPending}>
                Cancel booking
              </Button>
            )}
            {booking.status === "confirmed" && (
              <Button variant="secondary" onClick={() => void handleCancel()} disabled={cancelBooking.isPending}>
                Cancel booking
              </Button>
            )}
            {booking.status === "inquiry" && (
              <Button onClick={() => void handleConfirm()} disabled={confirmBooking.isPending}>
                {confirmBooking.isPending ? "Confirming…" : "Confirm"}
              </Button>
            )}
            {booking.status === "confirmed" && (
              <Button onClick={() => void handleComplete()} disabled={completeBooking.isPending}>
                {completeBooking.isPending ? "Completing…" : "Mark completed"}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
